import * as React from "react";
import { flushSync } from "react-dom";
import { useAction, useQuery } from "convex/react";
import { api } from "$convex/convexApi";
import { toast } from "sonner";
import {
  Search,
  Truck,
  Mail,
  Calendar,
  Package,
  MapPin,
  Weight,
  Ruler,
  Boxes,
  ChevronDown,
  Copy,
  Check,
  Loader2,
} from "lucide-react";

import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Textarea } from "@/components/ui/textarea";
import { useAuthIdentity } from "@/contexts/UserProfileContext";
import { useAccessorials } from "@/hooks/useLogistics";
import { useOrderWithFullDetails, useOrders } from "@/hooks/useOrders";
import { useUsersForImpersonation } from "@/hooks/useUsers";
import { formatCurrency, formatStatusLabel as formatOrderStatusLabel } from "@/lib/format";
import { cn } from "@/lib/utils";
import {
  BOX_PALLET_RATE_THRESHOLD,
  PALLET_LENGTH,
  PALLET_WIDTH,
  calculateBoxPalletization,
  calculatePalletizedShipment,
  calculateParcelShipment,
  classifyShipment,
} from "$convex/helpers/shippingPackaging";

interface UserProfile {
  _id: string;
  name?: string | null;
  email?: string | null;
  company?: string | null;
  userId?: string;
}

interface ShippingAddress {
  _id: string;
  addressLine1?: string | null;
  addressLine2?: string | null;
  street?: string | null;
  city?: string | null;
  state?: string | null;
  postalCode?: string | null;
  company?: string | null;
  isDefault?: boolean;
}

interface QuoteWarehouse {
  id: string;
  name: string;
  postalCode?: string;
}

interface QuoteRate {
  id: string;
  carrier: string;
  serviceName: string;
  transitDays: number | null;
  totalPrice: number;
  carrierCode?: string;
}

interface QuoteResult {
  success: boolean;
  error?: string;
  rates: QuoteRate[];
  isParcel: boolean;
  refId?: string;
  requestDetails?: {
    weight: number;
    length: number;
    width: number;
    height: number;
    pieces: number;
    pallets?: number;
    packageType?: string;
    freightClass?: string;
    accessorials: string[];
  };
  warehouse?: { name: string };
}

interface LogisticsOrder {
  _id: string;
  userId?: string | null;
  orderNumber?: string | null;
  status?: string | null;
  totalAmount?: number | null;
  createdAt?: number | string | null;
  user?: {
    name?: string | null;
    email?: string | null;
    company?: string | null;
  } | null;
  payment?: {
    status?: string | null;
    method?: string | null;
  } | null;
  delivery?: {
    deliveryCharge?: number | null;
    deliveryAddress?: {
      address?: string | null;
      addressLine1?: string | null;
      street?: string | null;
      city?: string | null;
      state?: string | null;
      postalCode?: string | null;
    } | null;
  } | null;
}

interface OrderDetailItem {
  quantity?: number | null;
  unitWeight?: number | null;
  product?: {
    weight?: number | null;
  } | null;
}

interface OrderDetail {
  _id: string;
  orderNumber?: string | null;
  totalAmount?: number | null;
  items?: OrderDetailItem[];
  delivery?: { warehouseLocationId?: string | null } | null;
}


function toManualAddress(address?: {
  address?: string | null;
  addressLine1?: string | null;
  street?: string | null;
  city?: string | null;
  state?: string | null;
  postalCode?: string | null;
} | null) {
  return {
    address: address?.addressLine1 || address?.street || address?.address || "",
    city: address?.city || "",
    state: address?.state || "",
    postalCode: address?.postalCode || "",
  };
}

function getOrderCustomerId(order: LogisticsOrder | null | undefined): string {
  return String(order?.userId || "");
}

function isNoAccessorialOption(accessorial: { name?: string | null; description?: string | null }) {
  const haystack = `${accessorial.name || ""} ${accessorial.description || ""}`.toLowerCase();
  return haystack.includes("don't need") || haystack.includes("dont need") || haystack.includes("no accessorial");
}

function deriveShipmentFromOrder(orderDetail: OrderDetail | null | undefined) {
  const items = Array.isArray(orderDetail?.items) ? orderDetail.items : [];
  if (items.length === 0) return null;

  const normalizedItems = items
    .map((item) => {
      const quantity = Math.max(1, Math.round(Number(item?.quantity ?? 0) || 0));
      const unitWeight = Number(item?.unitWeight ?? item?.product?.weight ?? 0) || 0;
      return { quantity, unitWeight };
    })
    .filter((item) => item.quantity > 0);

  if (normalizedItems.length === 0) return null;

  const totalPieces = normalizedItems.reduce((sum, item) => sum + item.quantity, 0);
  const shipmentClassification = classifyShipment(
    normalizedItems.map((item) => ({ weight: item.unitWeight, quantity: item.quantity })),
  );

  if (shipmentClassification === "parcel") {
    const parcelShipment = calculateParcelShipment(
      normalizedItems.map((item) => ({ weight: item.unitWeight, quantity: item.quantity })),
    );

    return {
      weight: parcelShipment.totalWeight > 0 ? parcelShipment.totalWeight.toFixed(1) : "",
      length: String(parcelShipment.length),
      width: String(parcelShipment.width),
      height: String(parcelShipment.height),
      pieces: String(totalPieces),
      pallets: "",
      packageType: "Parcels",
      freightClass: "",
      isParcel: true,
    };
  }

  const palletizedShipment = calculatePalletizedShipment(
    normalizedItems.map((item) => ({ weight: item.unitWeight, quantity: item.quantity })),
  );

  return {
    weight: palletizedShipment.totalWeight > 0 ? palletizedShipment.totalWeight.toFixed(1) : "",
    length: String(PALLET_LENGTH),
    width: String(PALLET_WIDTH),
    height: String(palletizedShipment.heightPerPallet),
    pieces: String(totalPieces),
    pallets: String(palletizedShipment.totalPallets),
    packageType: "Pallets_48x40",
    freightClass: getFreightClassFromDensity(palletizedShipment.density),
    isParcel: false,
  };
}

function normalizeState(state: string): string {
  if (!state) return "";
  if (state.length === 2) return state.toUpperCase();
  const stateMap: Record<string, string> = {
    california: "CA",
    texas: "TX",
    "new york": "NY",
    florida: "FL",
    georgia: "GA",
    illinois: "IL",
    ohio: "OH",
    michigan: "MI",
    pennsylvania: "PA",
    arizona: "AZ",
    "north carolina": "NC",
    "new jersey": "NJ",
    virginia: "VA",
    washington: "WA",
    tennessee: "TN",
    indiana: "IN",
    missouri: "MO",
    maryland: "MD",
    wisconsin: "WI",
    colorado: "CO",
    minnesota: "MN",
    "south carolina": "SC",
    alabama: "AL",
    louisiana: "LA",
    kentucky: "KY",
    oregon: "OR",
    oklahoma: "OK",
    connecticut: "CT",
    utah: "UT",
    iowa: "IA",
    nevada: "NV",
    arkansas: "AR",
    mississippi: "MS",
    kansas: "KS",
    "new mexico": "NM",
    nebraska: "NE",
    "west virginia": "WV",
    idaho: "ID",
    hawaii: "HI",
    "new hampshire": "NH",
    maine: "ME",
    montana: "MT",
    "rhode island": "RI",
    delaware: "DE",
    "south dakota": "SD",
    "north dakota": "ND",
    alaska: "AK",
    vermont: "VT",
    wyoming: "WY",
  };
  return stateMap[state.toLowerCase()] || state.toUpperCase().slice(0, 2);
}

function getFreightClassFromDensity(density: number): string {
  if (density >= 50) return "50";
  if (density >= 35) return "55";
  if (density >= 30) return "60";
  if (density >= 22.5) return "65";
  if (density >= 15) return "70";
  if (density >= 12) return "85";
  if (density >= 10) return "92.5";
  if (density >= 8) return "100";
  if (density >= 6) return "125";
  if (density >= 4) return "175";
  if (density >= 2) return "300";
  return "400";
}

function calculateFreightClass(weight: number, length: number, width: number, height: number): string {
  const lengthFt = length / 12;
  const widthFt = width / 12;
  const heightFt = height / 12;
  const volumeCubicFt = lengthFt * widthFt * heightFt;
  if (volumeCubicFt <= 0) return "125";
  const density = weight / volumeCubicFt;
  return getFreightClassFromDensity(density);
}

function generateQuoteId(): string {
  return `SQ-${Date.now().toString(36).toUpperCase()}`;
}

function UserSelector({
  users,
  selectedUserId,
  onSelect,
}: {
  users: UserProfile[];
  selectedUserId: string;
  onSelect: (userId: string) => void;
}) {
  const [search, setSearch] = React.useState("");
  const [open, setOpen] = React.useState(false);
  const containerRef = React.useRef<HTMLDivElement>(null);

  React.useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const filtered = React.useMemo(() => {
    if (!search.trim()) return users.slice(0, 15);
    const q = search.toLowerCase();
    return users
      .filter(
        (u) =>
          (u.name?.toLowerCase() || "").includes(q) ||
          (u.email?.toLowerCase() || "").includes(q) ||
          (u.company?.toLowerCase() || "").includes(q),
      )
      .slice(0, 15);
  }, [users, search]);

  const selected = users.find((u) => u._id === selectedUserId);

  return (
    <div ref={containerRef} className="relative">
      <div
        className="flex items-center justify-between w-full rounded-md border px-3 py-2 text-sm cursor-pointer hover:bg-muted/50 min-h-[38px]"
        onClick={() => setOpen(!open)}
      >
        {selected ? (
          <div className="flex items-center gap-2">
            <span className="font-medium">{selected.name || selected.email}</span>
            {selected.company && (
              <span className="text-muted-foreground text-xs">({selected.company})</span>
            )}
          </div>
        ) : (
          <span className="text-muted-foreground">Select a client...</span>
        )}
        <ChevronDown className="h-4 w-4 text-muted-foreground" />
      </div>

      {open && (
        <div className="absolute z-50 mt-1 w-full rounded-md border bg-popover shadow-md">
          <div className="p-2">
            <div className="relative">
              <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search clients..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-8"
                autoFocus
              />
            </div>
          </div>
          <div className="max-h-[280px] overflow-auto">
            {filtered.length === 0 ? (
              <div className="p-2 text-sm text-muted-foreground">No clients found</div>
            ) : (
              filtered.map((user) => (
                <div
                  key={user._id}
                  className={cn(
                    "p-2 cursor-pointer hover:bg-muted flex items-center justify-between",
                    selectedUserId === user._id && "bg-muted",
                  )}
                  onClick={() => {
                    onSelect(user._id);
                    setOpen(false);
                  }}
                >
                  <div className="space-y-0.5">
                    <div className="text-sm font-medium">{user.name || user.email}</div>
                    <div className="text-xs text-muted-foreground">{user.company || user.email}</div>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function AddressSelector({
  addresses,
  selectedId,
  onSelect,
}: {
  addresses: ShippingAddress[];
  selectedId: string;
  onSelect: (id: string) => void;
}) {
  if (addresses.length === 0) return null;

  return (
    <div className="space-y-2">
      <Label className="text-xs text-muted-foreground">Saved Addresses</Label>
      <div className="grid gap-2">
        {addresses.map((addr) => {
          const addressLine = addr.addressLine1 || addr.street || "";
          const isSelected = selectedId === addr._id;
          return (
            <div
              key={addr._id}
              className={cn(
                "flex items-start gap-3 rounded-md border p-3 cursor-pointer transition-colors",
                isSelected ? "border-primary bg-primary/5" : "hover:bg-muted/50",
              )}
              onClick={() => onSelect(addr._id)}
            >
              <MapPin className="h-4 w-4 mt-0.5 text-muted-foreground shrink-0" />
              <div className="space-y-0.5">
                <div className="text-sm">
                  {addressLine}
                  {addr.addressLine2 ? `, ${addr.addressLine2}` : ""}
                </div>
                <div className="text-xs text-muted-foreground">
                  {addr.city}, {addr.state} {addr.postalCode}
                </div>
                {addr.isDefault && (
                  <Badge variant="outline" className="text-[10px] mt-1">
                    Default
                  </Badge>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function OrderSelector({
  orders,
  selectedOrderId,
  onSelect,
}: {
  orders: LogisticsOrder[];
  selectedOrderId: string;
  onSelect: (orderId: string) => void;
}) {
  const [search, setSearch] = React.useState("");
  const [open, setOpen] = React.useState(false);
  const containerRef = React.useRef<HTMLDivElement>(null);

  React.useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const filtered = React.useMemo(() => {
    if (!search.trim()) return orders.slice(0, 12);
    const q = search.toLowerCase();
    return orders
      .filter((order) =>
        (order.orderNumber?.toLowerCase() || "").includes(q) ||
        (order.user?.name?.toLowerCase() || "").includes(q) ||
        (order.user?.company?.toLowerCase() || "").includes(q) ||
        (order.user?.email?.toLowerCase() || "").includes(q),
      )
      .slice(0, 12);
  }, [orders, search]);

  const selectedOrder = orders.find((order) => order._id === selectedOrderId);

  return (
    <div ref={containerRef} className="relative">
      <div
        className="flex min-h-[38px] w-full cursor-pointer items-center justify-between rounded-md border px-3 py-2 text-sm hover:bg-muted/50"
        onClick={() => setOpen(!open)}
      >
        {selectedOrder ? (
          <div className="flex min-w-0 items-center gap-2">
            <span className="truncate font-mono">{selectedOrder.orderNumber || selectedOrder._id}</span>
            <span className="text-muted-foreground">•</span>
            <span className="truncate text-muted-foreground">
              {selectedOrder.user?.company || selectedOrder.user?.name || selectedOrder.user?.email || "Customer order"}
            </span>
          </div>
        ) : (
          <span className="text-muted-foreground">Optional: select an order...</span>
        )}
        <ChevronDown className="h-4 w-4 text-muted-foreground" />
      </div>

      {open && (
        <div className="absolute z-50 mt-1 w-full rounded-md border bg-popover shadow-md">
          <div className="p-2">
            <div className="relative">
              <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search orders..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-8"
                autoFocus
              />
            </div>
          </div>
          <div className="max-h-[320px] overflow-auto">
            {filtered.length === 0 ? (
              <div className="p-2 text-sm text-muted-foreground">No orders found</div>
            ) : (
              filtered.map((order) => (
                <div
                  key={order._id}
                  className={cn(
                    "flex cursor-pointer items-start justify-between p-2 hover:bg-muted",
                    selectedOrderId === order._id && "bg-muted",
                  )}
                  onClick={() => {
                    onSelect(order._id);
                    setOpen(false);
                  }}
                >
                  <div className="space-y-1">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="font-mono text-sm">{order.orderNumber || order._id}</span>
                      {order.status ? (
                        <Badge variant="outline" className="text-[10px]">
                          {formatOrderStatusLabel(order.status)}
                        </Badge>
                      ) : null}
                    </div>
                    <div className="text-xs text-muted-foreground">
                      {order.user?.company || order.user?.name || order.user?.email || "Customer order"}
                    </div>
                    <div className="text-xs text-muted-foreground">
                      Total {formatCurrency(order.totalAmount || 0)}
                      {typeof order.delivery?.deliveryCharge === "number"
                        ? ` • Shipping ${formatCurrency(order.delivery.deliveryCharge)}`
                        : ""}
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default function LogisticsShippingQuotesPage() {
  const { authUserId } = useAuthIdentity();
  const usersResult = useUsersForImpersonation();
  const ordersResult = useOrders(500);
  const accessorialsResult = useAccessorials();
  const warehouses = useQuery(api.shipping.getWarehouseLocations, {}) as QuoteWarehouse[] | undefined;

  const getQuoteAction = useAction(api.shipping.getStandaloneShippingQuote);
  const sendEmailAction = useAction(api.shipping.sendStandaloneShippingQuoteEmail);

  const [selectedUserId, setSelectedUserId] = React.useState("");
  const [selectedOrderId, setSelectedOrderId] = React.useState("");
  const [warehouseOverride, setWarehouseOverride] = React.useState<{ orderId: string; warehouseId: string } | null>(null);
  const selectOrder = React.useCallback((orderId: string) => {
    setSelectedOrderId(orderId);
    setWarehouseOverride(null);
  }, []);
  const carrierBlocklist = useQuery(
    api.logistics.getCarrierBlocklistForUser,
    authUserId && selectedUserId ? { authUserId, userId: selectedUserId as any } : "skip",
  );
  const blockedCarrierIds = carrierBlocklist?.blockedCarriers.map((carrier: { carrierId: string }) => String(carrier.carrierId)).sort();
  const [addressMode, setAddressMode] = React.useState<"saved" | "manual">("manual");
  const [selectedAddressId, setSelectedAddressId] = React.useState("");
  const orderDetailResult = useOrderWithFullDetails(selectedOrderId || undefined);

  // Manual address fields
  const [manualAddress, setManualAddress] = React.useState({
    address: "",
    city: "",
    state: "",
    postalCode: "",
  });

  // Shipment details
  const [shipment, setShipment] = React.useState({
    weight: "",
    length: "48",
    width: "40",
    height: "",
    pieces: "1",
    pallets: "1",
    packageType: "Pallets_48x40",
    freightClass: "",
    isParcel: false,
  });

  const [pickupDate, setPickupDate] = React.useState(() => {
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    return tomorrow.toISOString().split("T")[0];
  });

  const [selectedAccessorials, setSelectedAccessorials] = React.useState<string[]>([]);
  const [quoteNotes, setQuoteNotes] = React.useState("");

  const [quoteResult, setQuoteResult] = React.useState<QuoteResult | null>(null);
  const [isLoading, setIsLoading] = React.useState(false);
  const [isEmailing, setIsEmailing] = React.useState(false);
  const [quoteId, setQuoteId] = React.useState("");
  const [copiedId, setCopiedId] = React.useState(false);
  const lastPrefilledOrderIdRef = React.useRef<string | null>(null);

  // Fetch saved addresses when user changes
  const savedAddresses = useQuery(
    api.userShippingInfo.getUserShippingInfoForAdmin,
    authUserId && selectedUserId
      ? { authUserId, targetUserId: selectedUserId as any, type: "shipping" }
      : "skip",
  );

  const users: UserProfile[] = React.useMemo(() => {
    if (!usersResult) return [];
    return Array.isArray(usersResult) ? usersResult : [];
  }, [usersResult]);

  const orders: LogisticsOrder[] = React.useMemo(() => {
    if (!ordersResult) return [];
    return Array.isArray(ordersResult) ? (ordersResult as LogisticsOrder[]) : [];
  }, [ordersResult]);

  const accessorials = React.useMemo(() => {
    if (!accessorialsResult) return [];
    return Array.isArray(accessorialsResult) ? accessorialsResult : [];
  }, [accessorialsResult]);

  const addresses: ShippingAddress[] = React.useMemo(() => {
    if (!savedAddresses) return [];
    return Array.isArray(savedAddresses) ? savedAddresses : [];
  }, [savedAddresses]);

  const toggleAccessorial = React.useCallback(
    (accessorial: { id: string; name?: string | null; description?: string | null }, checked: boolean) => {
      const accessorialId = String(accessorial.id);
      const isNoneOption = isNoAccessorialOption(accessorial);

      setSelectedAccessorials((prev) => {
        const next = new Set(prev.map(String));

        if (!checked) {
          next.delete(accessorialId);
          return Array.from(next);
        }

        if (isNoneOption) {
          return [accessorialId];
        }

        next.add(accessorialId);
        for (const existing of accessorials) {
          if (isNoAccessorialOption(existing)) {
            next.delete(String(existing.id));
          }
        }
        return Array.from(next);
      });
    },
    [accessorials],
  );

  // Auto-calculate freight class when dims/weight change (for LTL)
  React.useEffect(() => {
    if (shipment.isParcel) return;
    const weight = parseFloat(shipment.weight);
    const length = parseFloat(shipment.length);
    const width = parseFloat(shipment.width);
    const height = parseFloat(shipment.height);
    if (weight > 0 && length > 0 && width > 0 && height > 0) {
      const calculated = calculateFreightClass(weight, length, width, height);
      setShipment((s) => ({ ...s, freightClass: calculated }));
    }
  }, [shipment.weight, shipment.length, shipment.width, shipment.height, shipment.isParcel]);

  // Auto-fill manual address when saved address selected
  React.useEffect(() => {
    if (addressMode === "saved" && selectedAddressId) {
      const addr = addresses.find((a) => a._id === selectedAddressId);
      if (addr) {
        setManualAddress(toManualAddress(addr));
      }
    }
  }, [addressMode, selectedAddressId, addresses]);

  // Auto-select the default saved address when a client is chosen.
  React.useEffect(() => {
    if (!selectedUserId || selectedOrderId) return;
    if (savedAddresses === undefined) return;
    if (!Array.isArray(savedAddresses) || savedAddresses.length === 0) return;

    const preferredAddress =
      savedAddresses.find((address) => address.isDefault) ??
      savedAddresses[0];

    setAddressMode("saved");
    setSelectedAddressId(preferredAddress._id);
    setManualAddress(toManualAddress(preferredAddress));
  }, [savedAddresses, selectedOrderId, selectedUserId]);

  const selectedUser = React.useMemo(
    () => users.find((u) => u._id === selectedUserId),
    [users, selectedUserId],
  );

  const availableOrders = React.useMemo(() => {
    if (!selectedUserId) return orders;
    return orders.filter((order) => getOrderCustomerId(order) === selectedUserId);
  }, [orders, selectedUserId]);

  const selectedOrder = React.useMemo(
    () => orders.find((order) => order._id === selectedOrderId) ?? null,
    [orders, selectedOrderId],
  );

  const selectedOrderDetail = React.useMemo(
    () => (orderDetailResult as OrderDetail | null | undefined) ?? null,
    [orderDetailResult],
  );

  React.useEffect(() => {
    if (!selectedOrder) return;

    const orderCustomerId = getOrderCustomerId(selectedOrder);
    if (orderCustomerId && orderCustomerId !== selectedUserId) {
      setSelectedUserId(orderCustomerId);
    }

    const orderAddress = toManualAddress(selectedOrder.delivery?.deliveryAddress);
    if (orderAddress.address && orderAddress.city && orderAddress.state && orderAddress.postalCode) {
      setAddressMode("manual");
      setSelectedAddressId("");
      setManualAddress(orderAddress);
    }
  }, [selectedOrder, selectedUserId]);

  React.useEffect(() => {
    if (!selectedOrderId) {
      lastPrefilledOrderIdRef.current = null;
      return;
    }
    if (!selectedOrderDetail || lastPrefilledOrderIdRef.current === selectedOrderId) return;

    const shipmentFromOrder = deriveShipmentFromOrder(selectedOrderDetail);
    if (!shipmentFromOrder) return;

    setShipment((current) => ({
      ...current,
      ...shipmentFromOrder,
    }));
    lastPrefilledOrderIdRef.current = selectedOrderId;
  }, [selectedOrderDetail, selectedOrderId]);

  const selectedWarehouseId = warehouseOverride?.orderId === selectedOrderId
    ? warehouseOverride.warehouseId
    : selectedOrderId && selectedOrderDetail?._id === selectedOrderId
      ? selectedOrderDetail.delivery?.warehouseLocationId ?? ""
      : "";
  const selectedWarehouse = warehouses?.find((warehouse) => warehouse.id === selectedWarehouseId);

  const canGetQuote = React.useMemo(() => {
    return (
      selectedWarehouse &&
      (!selectedUserId || carrierBlocklist !== undefined) &&
      manualAddress.address &&
      manualAddress.city &&
      manualAddress.state &&
      manualAddress.postalCode &&
      parseFloat(shipment.weight) > 0 &&
      parseFloat(shipment.length) > 0 &&
      parseFloat(shipment.width) > 0 &&
      parseFloat(shipment.height) > 0 &&
      parseInt(shipment.pieces) > 0 &&
      pickupDate
    );
  }, [manualAddress, shipment, pickupDate, selectedWarehouse, selectedUserId, carrierBlocklist]);

  const quoteInputFingerprint = React.useMemo(
    () => JSON.stringify({
      selectedUserId,
      selectedOrderId,
      selectedWarehouseId,
      blockedCarrierIds,
      manualAddress,
      pickupDate,
      selectedAccessorials: selectedAccessorials.map(String).sort(),
      shipment,
    }),
    [manualAddress, pickupDate, selectedAccessorials, selectedUserId, selectedOrderId, selectedWarehouseId, blockedCarrierIds, shipment],
  );

  const quoteRevisionRef = React.useRef(0);
  React.useEffect(() => {
    quoteRevisionRef.current += 1;
    setQuoteResult(null);
    setQuoteId("");
    setCopiedId(false);
  }, [quoteInputFingerprint]);

  const handleGetQuote = React.useCallback(async () => {
    if (!canGetQuote) {
      toast.error("Please fill in all required fields.");
      return;
    }

    setIsLoading(true);
    setQuoteResult(null);

    try {
      let isParcel = shipment.isParcel;
      let weight = parseFloat(shipment.weight);
      let length = parseFloat(shipment.length);
      let width = parseFloat(shipment.width);
      let height = parseFloat(shipment.height);
      const pieces = parseInt(shipment.pieces);
      let pallets = parseInt(shipment.pallets || "1");
      let freightClass = shipment.freightClass || "125";

      if (isParcel && pieces >= BOX_PALLET_RATE_THRESHOLD) {
        const palletizedBoxes = calculateBoxPalletization(pieces, weight);
        isParcel = false;
        weight = palletizedBoxes.totalWeight;
        length = PALLET_LENGTH;
        width = PALLET_WIDTH;
        height = palletizedBoxes.heightPerPallet;
        pallets = palletizedBoxes.totalPallets;
        freightClass = getFreightClassFromDensity(palletizedBoxes.density);

        // Commit automatic palletization before capturing the request revision.
        flushSync(() => setShipment((current) => ({
          ...current,
          weight: palletizedBoxes.totalWeight.toFixed(1),
          length: String(PALLET_LENGTH),
          width: String(PALLET_WIDTH),
          height: String(palletizedBoxes.heightPerPallet),
          pallets: String(palletizedBoxes.totalPallets),
          packageType: "Pallets_48x40",
          freightClass,
          isParcel: false,
        })));
        toast.info("10+ box shipments are quoted as pallet/LTL rates.");
      }

      const accessorialCodes = accessorials
        .filter((a: any) => selectedAccessorials.includes(String(a.id)))
        .map((a: any) => a.shippeekCode || a.code)
        .filter(Boolean);

      const requestRevision = quoteRevisionRef.current;
      const result = await getQuoteAction({
        warehouseId: selectedWarehouseId as any,
        destPostalCode: manualAddress.postalCode,
        destAddress: manualAddress.address,
        destState: normalizeState(manualAddress.state),
        destCity: manualAddress.city,
        pickupDate,
        accessorials: accessorialCodes,
        shipment: {
          description: selectedOrder?.orderNumber
            ? `Shipping quote for order ${selectedOrder.orderNumber}`
            : `Shipping quote for ${selectedUser?.name || selectedUser?.company || "standalone shipment"}`,
          weight,
          length,
          width,
          height,
          pieces,
          pallets: isParcel ? undefined : pallets,
          packageType: isParcel ? "Parcels" : "Pallets_48x40",
          freightClass: isParcel ? undefined : freightClass,
          isParcel,
        },
        userId: selectedUserId ? (selectedUserId as any) : undefined,
      });

      if (requestRevision !== quoteRevisionRef.current) return;

      if (result?.success && Array.isArray(result.rates)) {
        setQuoteResult(result as QuoteResult);
        setQuoteId(generateQuoteId());
        toast.success(`Found ${result.rates.length} shipping rates`);
      } else {
        toast.error(result?.error || "Failed to get shipping rates");
      }
    } catch (err: any) {
      toast.error(err?.message || "Failed to get shipping rates");
    } finally {
      setIsLoading(false);
    }
  }, [
    canGetQuote,
    shipment,
    accessorials,
    selectedAccessorials,
    manualAddress,
    pickupDate,
    selectedUserId,
    selectedUser,
    getQuoteAction,
    selectedOrder,
    selectedWarehouseId,
  ]);

  const handleEmailQuote = React.useCallback(async () => {
    if (!quoteResult || !selectedUser || !quoteResult.success) return;

    const recipient = selectedUser.email;
    if (!recipient) {
      toast.error("Selected client has no email address.");
      return;
    }

    setIsEmailing(true);
    try {
      const shipmentDetail = quoteResult.requestDetails;
      if (!shipmentDetail) {
        toast.error("Quote details missing");
        return;
      }

      const result = await sendEmailAction({
        customerEmail: recipient,
        customerName: selectedUser.name || selectedUser.company || recipient,
        quoteId,
        destination: {
          address: manualAddress.address,
          city: manualAddress.city,
          state: manualAddress.state,
          postalCode: manualAddress.postalCode,
        },
        shipment: {
          weight: shipmentDetail.weight,
          length: shipmentDetail.length,
          width: shipmentDetail.width,
          height: shipmentDetail.height,
          pieces: shipmentDetail.pieces,
          pallets: shipmentDetail.pallets,
          packageType: shipmentDetail.packageType || "Pallets_48x40",
          freightClass: shipmentDetail.freightClass,
        },
        accessorials: !quoteResult.isParcel && selectedAccessorials.length > 0
          ? accessorials
              .filter((a: any) => selectedAccessorials.includes(String(a.id)))
              .map((a: any) => a.name)
          : [],
        rates: quoteResult.rates.map((r) => ({
          carrier: r.carrier,
          serviceName: r.serviceName,
          transitDays: r.transitDays ?? undefined,
          totalPrice: r.totalPrice,
        })),
        warehouseName: quoteResult.warehouse?.name || "Anthem Coffee",
        pickupDate,
        notes: quoteNotes || undefined,
      });

      if (result?.success) {
        toast.success("Shipping quote emailed to client.");
      } else {
        toast.error(result?.error || "Failed to send email.");
      }
    } catch (err: any) {
      toast.error(err?.message || "Failed to send email.");
    } finally {
      setIsEmailing(false);
    }
  }, [
    quoteResult,
    selectedUser,
    quoteId,
    manualAddress,
    selectedAccessorials,
    accessorials,
    pickupDate,
    quoteNotes,
    sendEmailAction,
  ]);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Shipping Quotes</h1>
          <p className="text-muted-foreground mt-1">
            Generate shipping estimates with optional client and order context
          </p>
        </div>
      </div>

      <div className="grid gap-6 lg:grid-cols-3 lg:items-start">
        {/* Left column - Form */}
        <div className="lg:col-span-2 space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Truck className="h-5 w-5" />
                Quote Request
              </CardTitle>
              <CardDescription>
                Start with manual shipment details, then optionally add a client or existing order for faster context
              </CardDescription>
              <div className="flex items-center gap-3 overflow-x-auto whitespace-nowrap text-xs text-muted-foreground">
                <span>100+ LTL carriers</span>
                <span className="text-muted-foreground/50">•</span>
                <span>Multi-carrier support</span>
                <span className="text-muted-foreground/50">•</span>
                <span>Accessorial add-ons</span>
              </div>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Client */}
              <div className="space-y-2">
                <div className="flex items-center justify-between gap-3">
                  <div>
                    <Label>Client (optional)</Label>
                    <p className="text-xs text-muted-foreground">
                      Choose a client to auto-fill their default shipping address and apply carrier preferences.
                    </p>
                  </div>
                  {selectedUserId ? (
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => {
                        setSelectedUserId("");
                        selectOrder("");
                        setAddressMode("manual");
                        setSelectedAddressId("");
                        setManualAddress({ address: "", city: "", state: "", postalCode: "" });
                      }}
                    >
                      Clear client
                    </Button>
                  ) : null}
                </div>
                <UserSelector
                  users={users}
                  selectedUserId={selectedUserId}
                  onSelect={(id) => {
                    setSelectedUserId(id);
                    selectOrder("");
                    setAddressMode("manual");
                    setSelectedAddressId("");
                    setManualAddress({ address: "", city: "", state: "", postalCode: "" });
                  }}
                />
              </div>

              <div className="space-y-2">
                <div className="flex items-center justify-between gap-3">
                  <div>
                    <Label>Order (optional)</Label>
                    <p className="text-xs text-muted-foreground">
                      {selectedUserId
                        ? `Showing ${availableOrders.length} recent order${availableOrders.length === 1 ? "" : "s"} for this client.`
                        : "Search recent orders for any client, or skip this step for a fully manual quote."}
                    </p>
                  </div>
                  {selectedOrderId ? (
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => selectOrder("")}
                    >
                      Clear order
                    </Button>
                  ) : null}
                </div>
                <OrderSelector
                  orders={availableOrders}
                  selectedOrderId={selectedOrderId}
                  onSelect={selectOrder}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="quote-origin">Origin warehouse</Label>
                <Select
                  value={selectedWarehouse?.id ?? ""}
                  onValueChange={(warehouseId) => setWarehouseOverride({ orderId: selectedOrderId, warehouseId })}
                >
                  <SelectTrigger id="quote-origin">
                    <SelectValue placeholder={warehouses === undefined ? "Loading warehouses..." : "Select origin warehouse"} />
                  </SelectTrigger>
                  <SelectContent>
                    {warehouses?.map((warehouse) => (
                      <SelectItem key={warehouse.id} value={warehouse.id}>
                        {warehouse.name} · {warehouse.postalCode}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-xs text-muted-foreground">Rates and carrier availability depend on the pickup warehouse.</p>
              </div>

              {selectedOrder ? (
                <div className="grid gap-3 rounded-lg border bg-muted/30 p-4 sm:grid-cols-3">
                  <div className="space-y-1">
                    <p className="text-xs text-muted-foreground">Order</p>
                    <p className="font-mono text-sm">{selectedOrder.orderNumber || selectedOrder._id}</p>
                    {selectedOrder.status ? (
                      <Badge variant="outline" className="text-[10px]">
                        {formatOrderStatusLabel(selectedOrder.status)}
                      </Badge>
                    ) : null}
                  </div>
                  <div className="space-y-1">
                    <p className="text-xs text-muted-foreground">Order total</p>
                    <p className="text-sm font-semibold">{formatCurrency(selectedOrder.totalAmount || 0)}</p>
                    <p className="text-xs text-muted-foreground">
                      {selectedOrder.payment?.method
                        ? `${String(selectedOrder.payment.method).toUpperCase()} • ${selectedOrder.payment?.status ? formatOrderStatusLabel(String(selectedOrder.payment.status)) : "Payment status unknown"}`
                        : selectedOrder.payment?.status
                          ? formatOrderStatusLabel(String(selectedOrder.payment.status))
                          : "Payment status unknown"}
                    </p>
                  </div>
                  <div className="space-y-1">
                    <p className="text-xs text-muted-foreground">Current shipping</p>
                    <p className="text-sm font-semibold">
                      {formatCurrency(selectedOrder.delivery?.deliveryCharge || 0)}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      {selectedOrderDetail?.items?.length
                        ? `Loaded destination + shipment defaults from ${selectedOrderDetail.items.length} order item${selectedOrderDetail.items.length === 1 ? "" : "s"}.`
                        : "Selecting an order also pulls its destination when available."}
                    </p>
                  </div>
                </div>
              ) : null}

              {/* Address */}
              {selectedUserId && addresses.length > 0 && (
                <div className="flex items-center gap-2">
                  <Button
                    type="button"
                    size="sm"
                    variant={addressMode === "saved" ? "default" : "outline"}
                    onClick={() => setAddressMode("saved")}
                  >
                    Use Saved Address
                  </Button>
                  <Button
                    type="button"
                    size="sm"
                    variant={addressMode === "manual" ? "default" : "outline"}
                    onClick={() => setAddressMode("manual")}
                  >
                    Enter Manually
                  </Button>
                </div>
              )}

              {addressMode === "saved" && addresses.length > 0 && (
                <AddressSelector
                  addresses={addresses}
                  selectedId={selectedAddressId}
                  onSelect={setSelectedAddressId}
                />
              )}

              <div className="grid gap-4 sm:grid-cols-2">
                <div className="space-y-2 sm:col-span-2">
                  <Label>Street Address</Label>
                  <Input
                    placeholder="123 Main St"
                    value={manualAddress.address}
                    onChange={(e) =>
                      setManualAddress((p) => ({ ...p, address: e.target.value }))
                    }
                  />
                </div>
                <div className="space-y-2">
                  <Label>City</Label>
                  <Input
                    placeholder="City"
                    value={manualAddress.city}
                    onChange={(e) =>
                      setManualAddress((p) => ({ ...p, city: e.target.value }))
                    }
                  />
                </div>
                <div className="space-y-2">
                  <Label>State</Label>
                  <Input
                    placeholder="CA"
                    value={manualAddress.state}
                    onChange={(e) =>
                      setManualAddress((p) => ({ ...p, state: e.target.value }))
                    }
                  />
                </div>
                <div className="space-y-2">
                  <Label>Postal Code</Label>
                  <Input
                    placeholder="90210"
                    value={manualAddress.postalCode}
                    onChange={(e) =>
                      setManualAddress((p) => ({ ...p, postalCode: e.target.value }))
                    }
                  />
                </div>
              </div>

              {/* Package type toggle */}
              <div className="space-y-2">
                <Label>Shipment Type</Label>
                <div className="flex items-center gap-2">
                  <Button
                    type="button"
                    size="sm"
                    variant={!shipment.isParcel ? "default" : "outline"}
                    onClick={() =>
                      setShipment((s) => ({
                        ...s,
                        isParcel: false,
                        packageType: "Pallets_48x40",
                        length: "48",
                        width: "40",
                      }))
                    }
                  >
                    <Boxes className="mr-1 h-4 w-4" />
                    LTL / Pallet
                  </Button>
                  <Button
                    type="button"
                    size="sm"
                    variant={shipment.isParcel ? "default" : "outline"}
                    onClick={() => {
                      setShipment((s) => ({
                        ...s,
                        isParcel: true,
                        packageType: "Parcels",
                        length: "12",
                        width: "12",
                        height: "9",
                        pallets: "",
                      }));
                      setSelectedAccessorials([]);
                    }}
                  >
                    <Package className="mr-1 h-4 w-4" />
                    Parcel / Box
                  </Button>
                </div>
                <p className="text-xs text-muted-foreground">
                  10+ boxes automatically quote via pallet/LTL. Current estimate: 1 pallet per 48 boxes
                  (12 boxes per layer × 4 layers).
                </p>
              </div>

              {/* Shipment details */}
              <div className="grid gap-4 sm:grid-cols-3">
                <div className="space-y-2">
                  <Label className="flex items-center gap-1">
                    <Weight className="h-3 w-3" />
                    Weight (lbs)
                  </Label>
                  <Input
                    type="number"
                    step="0.1"
                    min="0"
                    placeholder="0.0"
                    value={shipment.weight}
                    onChange={(e) => setShipment((s) => ({ ...s, weight: e.target.value }))}
                  />
                </div>
                <div className="space-y-2">
                  <Label className="flex items-center gap-1">
                    <Ruler className="h-3 w-3" />
                    Length (in)
                  </Label>
                  <Input
                    type="number"
                    step="1"
                    min="0"
                    value={shipment.length}
                    onChange={(e) => setShipment((s) => ({ ...s, length: e.target.value }))}
                  />
                </div>
                <div className="space-y-2">
                  <Label className="flex items-center gap-1">
                    <Ruler className="h-3 w-3" />
                    Width (in)
                  </Label>
                  <Input
                    type="number"
                    step="1"
                    min="0"
                    value={shipment.width}
                    onChange={(e) => setShipment((s) => ({ ...s, width: e.target.value }))}
                  />
                </div>
                <div className="space-y-2">
                  <Label className="flex items-center gap-1">
                    <Ruler className="h-3 w-3" />
                    Height (in)
                  </Label>
                  <Input
                    type="number"
                    step="1"
                    min="0"
                    value={shipment.height}
                    onChange={(e) => setShipment((s) => ({ ...s, height: e.target.value }))}
                  />
                </div>
                <div className="space-y-2">
                  <Label className="flex items-center gap-1">
                    <Boxes className="h-3 w-3" />
                    Pieces
                  </Label>
                  <Input
                    type="number"
                    step="1"
                    min="1"
                    value={shipment.pieces}
                    onChange={(e) => setShipment((s) => ({ ...s, pieces: e.target.value }))}
                  />
                </div>
                {!shipment.isParcel && (
                  <div className="space-y-2">
                    <Label>Pallets</Label>
                    <Input
                      type="number"
                      step="1"
                      min="1"
                      value={shipment.pallets}
                      onChange={(e) => setShipment((s) => ({ ...s, pallets: e.target.value }))}
                    />
                  </div>
                )}
                {!shipment.isParcel && (
                  <div className="space-y-2 sm:col-span-1">
                    <Label>Freight Class</Label>
                    <Select
                      value={shipment.freightClass}
                      onValueChange={(v) => setShipment((s) => ({ ...s, freightClass: v }))}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Auto" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="50">50</SelectItem>
                        <SelectItem value="55">55</SelectItem>
                        <SelectItem value="60">60</SelectItem>
                        <SelectItem value="65">65</SelectItem>
                        <SelectItem value="70">70</SelectItem>
                        <SelectItem value="77.5">77.5</SelectItem>
                        <SelectItem value="85">85</SelectItem>
                        <SelectItem value="92.5">92.5</SelectItem>
                        <SelectItem value="100">100</SelectItem>
                        <SelectItem value="110">110</SelectItem>
                        <SelectItem value="125">125</SelectItem>
                        <SelectItem value="150">150</SelectItem>
                        <SelectItem value="175">175</SelectItem>
                        <SelectItem value="200">200</SelectItem>
                        <SelectItem value="250">250</SelectItem>
                        <SelectItem value="300">300</SelectItem>
                        <SelectItem value="400">400</SelectItem>
                      </SelectContent>
                    </Select>
                    {!shipment.freightClass && (
                      <p className="text-[11px] text-muted-foreground">Auto-calculated from density</p>
                    )}
                  </div>
                )}
              </div>

              {/* Pickup date */}
              <div className="space-y-2">
                <Label className="flex items-center gap-1">
                  <Calendar className="h-3 w-3" />
                  Pickup Date
                </Label>
                <Input
                  type="date"
                  value={pickupDate}
                  onChange={(e) => setPickupDate(e.target.value)}
                  min={new Date().toISOString().split("T")[0]}
                />
              </div>

              {/* Accessorials */}
              {!shipment.isParcel && (
                <div className="space-y-2">
                  <Label>Accessorials</Label>
                  <div className="grid gap-2 sm:grid-cols-2">
                    {accessorials.map((a: any) => {
                      const accessorialId = String(a.id);
                      const isChecked = selectedAccessorials.includes(accessorialId);
                      return (
                        <div
                          key={accessorialId}
                          className="flex cursor-pointer items-start gap-2 rounded-md p-1 hover:bg-muted/40"
                          onClick={() => toggleAccessorial(a, !isChecked)}
                        >
                          <Checkbox
                            id={`acc-${accessorialId}`}
                            checked={isChecked}
                            onClick={(event) => event.stopPropagation()}
                            onCheckedChange={(checked) => {
                              toggleAccessorial(a, checked === true);
                            }}
                          />
                          <label
                            htmlFor={`acc-${accessorialId}`}
                            className="cursor-pointer text-sm leading-tight"
                            onClick={(event) => {
                              event.preventDefault();
                              toggleAccessorial(a, !isChecked);
                            }}
                          >
                            {a.name}
                            <p className="text-xs text-muted-foreground">{a.description}</p>
                          </label>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Notes */}
              <div className="space-y-2">
                <Label>Quote Notes (included in email)</Label>
                <Textarea
                  placeholder="Optional notes for the client..."
                  value={quoteNotes}
                  onChange={(e) => setQuoteNotes(e.target.value)}
                  rows={3}
                />
              </div>

              <Button
                onClick={() => void handleGetQuote()}
                disabled={isLoading || !canGetQuote}
                className="w-full"
              >
                {isLoading ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Getting rates...
                  </>
                ) : (
                  <>
                    <Truck className="mr-2 h-4 w-4" />
                    Get Shipping Quotes
                  </>
                )}
              </Button>
            </CardContent>
          </Card>
        </div>

        {/* Right column - Results */}
        <div className="space-y-6 lg:sticky lg:top-6 lg:z-10 lg:self-start lg:bg-background">
          {quoteResult?.success && (
            <Card>
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-lg">Quote Results</CardTitle>
                  {quoteId && (
                    <div className="flex items-center gap-1">
                      <code className="text-xs bg-muted px-1.5 py-0.5 rounded">{quoteId}</code>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-6 w-6"
                        onClick={() => {
                          void navigator.clipboard.writeText(quoteId);
                          setCopiedId(true);
                          setTimeout(() => setCopiedId(false), 2000);
                        }}
                      >
                        {copiedId ? (
                          <Check className="h-3 w-3 text-green-600" />
                        ) : (
                          <Copy className="h-3 w-3" />
                        )}
                      </Button>
                    </div>
                  )}
                </div>
                <CardDescription>
                  {quoteResult.rates.length} rate{quoteResult.rates.length !== 1 ? "s" : ""} found
                  {quoteResult.isParcel ? " (Parcel)" : " (LTL)"}
                  {quoteResult.warehouse && <span className="block">From {quoteResult.warehouse.name}</span>}
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-3">
                {quoteResult.rates.map((rate, idx) => (
                  <div
                    key={rate.id}
                    className={cn(
                      "rounded-lg border p-3 transition-colors",
                      idx === 0 ? "border-primary bg-primary/5" : "hover:bg-muted/40",
                    )}
                  >
                    <div className="flex items-center justify-between">
                      <div className="space-y-0.5">
                        <div className="font-medium text-sm">{rate.carrier}</div>
                        <div className="text-xs text-muted-foreground">
                          {rate.serviceName}
                          {rate.transitDays ? ` • ${rate.transitDays} day${rate.transitDays > 1 ? "s" : ""}` : ""}
                        </div>
                      </div>
                      <div className="text-right">
                        <div className="font-bold text-lg">{formatCurrency(rate.totalPrice)}</div>
                        {idx === 0 && (
                          <Badge variant="secondary" className="text-[10px]">
                            Best rate
                          </Badge>
                        )}
                      </div>
                    </div>
                  </div>
                ))}

                {selectedUser?.email && (
                  <Button
                    className="w-full mt-2"
                    variant="outline"
                    onClick={() => void handleEmailQuote()}
                    disabled={isEmailing}
                  >
                    {isEmailing ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        Sending...
                      </>
                    ) : (
                      <>
                        <Mail className="mr-2 h-4 w-4" />
                        Email to {selectedUser.name || selectedUser.email}
                      </>
                    )}
                  </Button>
                )}

                {!selectedUser?.email && (
                  <p className="text-xs text-muted-foreground text-center">
                    Select a client with an email on file if you want to send this quote by email.
                  </p>
                )}
              </CardContent>
            </Card>
          )}

          {isLoading && !quoteResult && (
            <Card className="border-dashed">
              <CardContent className="py-8 text-center">
                <Loader2 className="h-8 w-8 text-primary mx-auto mb-3 animate-spin" />
                <p className="text-sm text-muted-foreground">Fetching shipping rates...</p>
              </CardContent>
            </Card>
          )}

          {quoteResult && !quoteResult.success && (
            <Card className="border-destructive">
              <CardHeader>
                <CardTitle className="text-lg text-destructive">Quote Failed</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-destructive">{quoteResult.error || "Unable to get rates"}</p>
              </CardContent>
            </Card>
          )}

          {!isLoading && !quoteResult && (
            <Card className="border-dashed">
              <CardContent className="py-8 text-center">
                <Truck className="h-8 w-8 text-muted-foreground mx-auto mb-3" />
                <p className="text-sm text-muted-foreground">
                  Fill in the form and click "Get Shipping Quotes" to see carrier rates here.
                </p>
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    </div>
  );
}
